import { translate } from "@/i18n";
import { desktopVoiceMessages } from "@/i18n/messages/desktop-voice";
export const TTS_ACOUSTIC_TAIL_MS = 250;
export const TTS_TERMINAL_WATCHDOG_MS = 120_000;
export const MAX_ACTIVE_TTS_UTTERANCES = 16;
const MAX_BARGE_IN_PARTIAL_CHARS = 2_000;

type AcousticEpochPhase =
  | "idle"
  | "speaking"
  | "tail"
  | "discarding"
  | "failed";

export type TtsEpochDiscardReason = "tts_acoustic_tail" | "user_interruption";
type TtsEpochResumeReason = "clean_boundary" | "user_interruption" | "stopped";

export type TtsEpochHandlers = {
  /** Commits the echo-contaminated native segment without dispatching it. */
  discardContaminatedSegment: (
    reason: TtsEpochDiscardReason,
  ) => Promise<boolean>;
  /** `confirmed`: the user's speech was already established, resume regardless. */
  resumeBargeInPartial: (text: string, confirmed: boolean) => void;
  onFailure: (error: string) => void;
  onSuspensionChange?: (
    change:
      | { suspended: true; utteranceId: string }
      | {
          suspended: false;
          reason: TtsEpochResumeReason;
          suspendedMs: number;
        },
  ) => void;
};

/**
 * Guard around agent TTS until either echo-cancelled PCM confirms user speech
 * or every overlapping utterance is terminal and recognition ACKs a clean
 * boundary. Interruption keeps the active native segment and its opening words.
 */
export class ContinuousVoiceTtsEpoch {
  private phase: AcousticEpochPhase = "idle";
  private handlers: TtsEpochHandlers | null = null;
  private activeUtteranceWatchdogs = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private epochToken = 0;
  private discardSequence = 0;
  private tailTimer: ReturnType<typeof setTimeout> | null = null;
  private contaminatedDuringDiscard = false;
  private bargeInPartial = "";
  private acousticBargeInPartial = "";
  private resumeConfirmed = false;
  private distrustsSegmentText = false;
  private suspendedAt: number | null = null;

  /** Wired once by the lifecycle; the handlers outlive every epoch. */
  configure(handlers: TtsEpochHandlers): void {
    this.handlers = handlers;
  }

  begin(utteranceId: string): boolean {
    const normalized = utteranceId.trim();
    if (!normalized || this.phase === "failed") return false;
    if (this.activeUtteranceWatchdogs.has(normalized)) return false;
    if (this.activeUtteranceWatchdogs.size >= MAX_ACTIVE_TTS_UTTERANCES) {
      this.failWith(translate(desktopVoiceMessages, "errorPlaybackOverlap"));
      return false;
    }
    if (this.phase === "idle") {
      this.epochToken += 1;
      this.suspendedAt = Date.now();
      this.handlers?.onSuspensionChange?.({
        suspended: true,
        utteranceId: normalized,
      });
    }
    if (this.phase === "tail") this.cancelTail();
    if (this.phase === "discarding") {
      this.contaminatedDuringDiscard = true;
    } else {
      this.phase = "speaking";
    }
    const watchdog = setTimeout(() => {
      if (!this.activeUtteranceWatchdogs.has(normalized)) return;
      this.failWith(
        translate(desktopVoiceMessages, "errorPlaybackNoCompletion"),
      );
    }, TTS_TERMINAL_WATCHDOG_MS);
    this.activeUtteranceWatchdogs.set(normalized, watchdog);
    return true;
  }

  finish(utteranceId: string): boolean {
    const normalized = utteranceId.trim();
    const watchdog = this.activeUtteranceWatchdogs.get(normalized);
    if (watchdog === undefined) return false;
    clearTimeout(watchdog);
    this.activeUtteranceWatchdogs.delete(normalized);
    if (this.phase === "discarding") return true;
    if (this.activeUtteranceWatchdogs.size > 0) {
      this.phase = "speaking";
      return true;
    }
    this.scheduleTail();
    return true;
  }

  retainBargeInPartial(text: string): void {
    if (!this.isSuspendingDetector() || this.distrustsSegmentText) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    this.bargeInPartial = trimmed.slice(0, MAX_BARGE_IN_PARTIAL_CHARS);
  }

  interruptForUserSpeech(): string {
    const partial = this.acousticBargeInPartial || this.bargeInPartial;
    this.reset("user_interruption");
    return partial;
  }

  /**
   * The user spoke over playback that was just cancelled, on a capture whose
   * recognizer heard that playback: cut the contaminated segment now instead
   * of waiting for the terminal event and tail. Every utterance is forgotten
   * first, so a late `cancelled` receipt cannot start a second discard in the
   * middle of the user's sentence. A confirmed transcript is carried across
   * the cut and may still be revised until it is acknowledged; `null` means
   * nothing recognized in this segment can be told apart from Denker's own
   * words, so the user's segment restarts empty whatever arrives meanwhile.
   */
  cutForUserSpeech(partial: string | null): boolean {
    if (this.phase !== "speaking" && this.phase !== "tail") return false;
    this.cancelTail();
    this.clearUtteranceWatchdogs();
    this.bargeInPartial = (partial ?? "")
      .trim()
      .slice(0, MAX_BARGE_IN_PARTIAL_CHARS);
    this.acousticBargeInPartial = "";
    this.resumeConfirmed = true;
    this.distrustsSegmentText = partial === null;
    this.startDiscard(this.epochToken, "user_interruption");
    return true;
  }

  retainAcousticBargeInPartial(text: string): void {
    if (!this.isSuspendingDetector()) return;
    this.acousticBargeInPartial = text
      .trim()
      .slice(0, MAX_BARGE_IN_PARTIAL_CHARS);
  }

  deferTailForSpeech(): void {
    if (this.phase === "tail") this.scheduleTail();
  }

  fail(): void {
    this.cancelTail();
    this.clearUtteranceWatchdogs();
    this.epochToken += 1;
    this.phase = "failed";
    // Capture has ended; the reset before the next start resumes nothing.
    this.suspendedAt = null;
    this.clearBufferedSpeech();
  }

  reset(reason: TtsEpochResumeReason = "stopped"): void {
    this.cancelTail();
    this.clearUtteranceWatchdogs();
    this.epochToken += 1;
    this.contaminatedDuringDiscard = false;
    this.clearBufferedSpeech();
    this.returnToIdle(reason);
  }

  isSuspendingDetector(): boolean {
    return this.phase !== "idle";
  }

  /** When the playback suspending the detector began; null when none does. */
  suspendedSince(): number | null {
    return this.suspendedAt;
  }

  /**
   * True while a cut is under way whose acknowledgement will re-seed the
   * user's segment (the conditions `afterDiscardAck` resumes under). Until
   * then the user counts as speaking: the session stays in `speech` and keeps
   * its segment start instead of passing through a quiet state.
   */
  resumesConfirmedSpeechAfterDiscard(): boolean {
    return (
      this.phase === "discarding" &&
      this.resumeConfirmed &&
      this.activeUtteranceWatchdogs.size === 0 &&
      !this.contaminatedDuringDiscard
    );
  }

  /** Whether the segment being cut hands its text on to the next one. */
  carriesTextForward(): boolean {
    return this.phase === "discarding" && this.bargeInPartial !== "";
  }

  matches(utteranceId: string): boolean {
    return this.activeUtteranceWatchdogs.has(utteranceId.trim());
  }

  /** The utterance playing now, or null once every one is terminal. */
  activeUtterance(): string | null {
    return this.activeUtteranceWatchdogs.keys().next().value ?? null;
  }

  currentPhase(): AcousticEpochPhase {
    return this.phase;
  }

  private returnToIdle(reason: TtsEpochResumeReason): void {
    this.phase = "idle";
    const suspendedAt = this.suspendedAt;
    this.suspendedAt = null;
    if (suspendedAt === null) return;
    this.handlers?.onSuspensionChange?.({
      suspended: false,
      reason,
      suspendedMs: Date.now() - suspendedAt,
    });
  }

  private scheduleTail(): void {
    if (!this.handlers) {
      this.phase = "failed";
      return;
    }
    const token = this.epochToken;
    this.phase = "tail";
    this.cancelTail();
    this.tailTimer = setTimeout(() => {
      this.tailTimer = null;
      if (this.epochToken !== token || this.phase !== "tail") return;
      this.startDiscard(token, "tts_acoustic_tail");
    }, TTS_ACOUSTIC_TAIL_MS);
  }

  private startDiscard(token: number, reason: TtsEpochDiscardReason): void {
    const handlers = this.handlers;
    if (!handlers) {
      this.phase = "failed";
      return;
    }
    this.phase = "discarding";
    this.contaminatedDuringDiscard = false;
    const sequence = ++this.discardSequence;
    void handlers
      .discardContaminatedSegment(reason)
      .then((accepted) => {
        if (!this.isCurrentDiscard(token, sequence)) return;
        if (!accepted) {
          this.phase = "failed";
          return;
        }
        this.afterDiscardAck();
      })
      .catch(() => {
        if (this.isCurrentDiscard(token, sequence)) this.phase = "failed";
      });
  }

  private afterDiscardAck(): void {
    if (this.activeUtteranceWatchdogs.size > 0) {
      this.phase = "speaking";
      return;
    }
    if (this.contaminatedDuringDiscard) {
      this.scheduleTail();
      return;
    }
    const partial = this.bargeInPartial;
    const confirmed = this.resumeConfirmed;
    this.clearBufferedSpeech();
    this.returnToIdle(confirmed ? "user_interruption" : "clean_boundary");
    // A cut always re-seeds the detector, even with no text: the session was
    // put in `speech` at the interruption and needs a segment to commit.
    if (partial || confirmed) {
      this.handlers?.resumeBargeInPartial(partial, confirmed);
    }
  }

  private isCurrentDiscard(token: number, sequence: number): boolean {
    return (
      this.epochToken === token &&
      this.discardSequence === sequence &&
      this.phase === "discarding"
    );
  }

  private cancelTail(): void {
    if (this.tailTimer) clearTimeout(this.tailTimer);
    this.tailTimer = null;
  }

  private clearUtteranceWatchdogs(): void {
    for (const watchdog of this.activeUtteranceWatchdogs.values()) {
      clearTimeout(watchdog);
    }
    this.activeUtteranceWatchdogs.clear();
  }

  private failWith(error: string): void {
    this.fail();
    this.handlers?.onFailure(error);
  }

  private clearBufferedSpeech(): void {
    this.bargeInPartial = "";
    this.acousticBargeInPartial = "";
    this.resumeConfirmed = false;
    this.distrustsSegmentText = false;
  }
}

export const continuousVoiceTtsEpoch = new ContinuousVoiceTtsEpoch();
